/**
 * frontend/src/components/BreakingArtistOverlay.jsx
 * SoundDAO Sprint 5A M6 — Full-screen overlay when a player breaks an artist.
 *
 * Props:
 *   notification — { message, creator_id, badge_tier, sound_bonus }
 *   creatorName  — string
 *   onDismiss    — () => void
 *   isFirstTime  — bool, true = full overlay, false = don't render
 */

import React, { useState } from 'react';
import { PUBLIC_APP_URL } from '../lib/constants';

const BADGE_LABELS = {
  first_scout: '🥇 First Scout',
  early_scout: '🥈 Early Scout',
  scout:       '🥉 Scout',
};

export default function BreakingArtistOverlay({ notification, creatorName, onDismiss, isFirstTime }) {
  const [copied, setCopied] = useState(false);

  if (!isFirstTime || !notification) return null;

  const badgeLabel = BADGE_LABELS[notification.badge_tier] || notification.badge_tier;
  const shareText = `I just broke ${creatorName} on SoundDAO 🔥 ${badgeLabel}. I recruited them first. ${PUBLIC_APP_URL}`;

  const handleShareX = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
      '_blank',
      'noopener'
    );
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareText)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        window.prompt('Copy your Breaking Artist message:', shareText);
      });
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,0.85)',
    }} onClick={e => e.stopPropagation()}>
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        borderRadius: '20px', padding: '40px 32px', maxWidth: '400px', width: '90%',
        textAlign: 'center', color: '#fff', position: 'relative',
        boxShadow: '0 0 60px rgba(255,68,68,0.3)',
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🔥</div>
        <div style={{
          background: 'linear-gradient(135deg, #ff4444, #ff6b6b)',
          color: '#fff', fontWeight: 'bold', fontSize: '0.8rem',
          padding: '4px 16px', borderRadius: '20px', display: 'inline-block', marginBottom: '16px',
          textTransform: 'uppercase', letterSpacing: '1px',
        }}>
          YOU BROKE THIS ARTIST
        </div>
        <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', margin: '8px 0', color: '#fff' }}>
          {creatorName}
        </h2>
        {notification.message && (
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: '8px 0', fontStyle: 'italic' }}>
            &ldquo;{notification.message}&rdquo;
          </p>
        )}
        <div style={{
          background: 'rgba(255,255,255,0.08)', borderRadius: '12px',
          padding: '12px', margin: '16px 0',
        }}>
          <div style={{ fontSize: '1.1rem', marginBottom: '4px' }}>{badgeLabel}</div>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#f97316' }}>
            +{(notification.sound_bonus || 0).toLocaleString()} SOUND
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '16px' }}>
          <button onClick={handleShareX} style={{
            background: '#000', color: '#fff', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontWeight: 'bold',
            fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '6px',
          }}>
            𝕏 Share on X
          </button>
          <button onClick={handleCopy} style={{
            background: 'rgba(255,255,255,0.1)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: '10px', padding: '10px 20px', cursor: 'pointer', fontSize: '0.85rem',
          }}>
            {copied ? '✓ Copied!' : 'Copy'}
          </button>
        </div>
        <button onClick={onDismiss} style={{
          background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)',
          cursor: 'pointer', fontSize: '0.85rem', padding: '8px',
        }}>
          Maybe later
        </button>
      </div>
    </div>
  );
}
